import React, { useMemo, memo, useCallback, useRef } from 'react';
import { useScrollPosition } from '../../../hooks/useScrollPosition';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trophy, Zap, Target, Calendar, Award, Flame, TrendingUp, Clock, BarChart2 } from 'lucide-react';
import { BADGES } from '../../gamification/hooks/useGameification';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';
import { haptic } from '../../../shared/utils/haptics';

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Reads completed tasks map from localStorage.
 * Returns {} on any error.
 */
function readCompleted() {
  try {
    return JSON.parse(localStorage.getItem('adhd-planner-completed') || '{}');
  } catch { return {}; }
}

/**
 * Reads tasks array from localStorage.
 */
function readTasks() {
  try {
    return JSON.parse(localStorage.getItem('adhd-planner-custom-tasks') || '[]');
  } catch { return []; }
}

/**
 * Returns the Monday-based weekOffset and dayIndex for a given Date.
 * weekOffset is relative to the week containing `now`.
 */
function getWeekOffsetAndDay(targetDate, now = new Date()) {
  const todayMon = getMondayOf(now);
  const targetMon = getMondayOf(targetDate);
  const diffDays = Math.round((targetMon - todayMon) / 86400000);
  const weekOffset = diffDays / 7;
  // dayIndex: 0=Mon … 6=Sun
  const day = targetDate.getDay();
  const dayIndex = day === 0 ? 6 : day - 1;
  return { weekOffset, dayIndex };
}

function getMondayOf(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Compute per-day completion counts for the last N days.
 * Returns array of { dateStr, count, weekday } newest last.
 */
function getDailyCompletions(nDays = 7) {
  const completed = readCompleted();
  const now = new Date();
  const result = [];

  for (let i = nDays - 1; i >= 0; i--) {
    const date = new Date(now);
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);

    const { weekOffset, dayIndex } = getWeekOffsetAndDay(date, now);
    const prefix = `${weekOffset}-${dayIndex}-`;
    const count = Object.keys(completed).filter(k => k.startsWith(prefix)).length;

    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    result.push({
      dateStr: date.toISOString().split('T')[0],
      count,
      weekday: dayNames[date.getDay()],
      isToday: i === 0
    });
  }

  return result;
}

/**
 * Compute per-week total completions for the last N weeks.
 * Returns array of { weekLabel, count } oldest first.
 */
function getWeeklyCompletions(nWeeks = 12) {
  const completed = readCompleted();
  const result = [];

  for (let w = -(nWeeks - 1); w <= 0; w++) {
    const count = Object.keys(completed).filter(k => k.startsWith(`${w}-`)).length;
    result.push({ weekOffset: w, count });
  }

  return result;
}

/**
 * Compute category breakdown from tasks.
 * Returns array of { category, count, color }.
 */
function getCategoryBreakdown() {
  const tasks = readTasks();
  const cats = { vital: 0, chore: 0, pleasure: 0, reward: 0, work: 0 };
  tasks.forEach(t => {
    if (cats[t.category] !== undefined) cats[t.category]++;
    else cats.work++;
  });

  const colors = {
    vital: '#f87171',
    chore: '#fb923c',
    pleasure: '#4ade80',
    reward: '#c084fc',
    work: '#60a5fa'
  };
  const labels = {
    vital: 'Vital', chore: 'Corvée', pleasure: 'Plaisir', reward: 'Récompense', work: 'Travail'
  };

  return Object.entries(cats)
    .filter(([, c]) => c > 0)
    .map(([cat, count]) => ({ category: cat, count, color: colors[cat], label: labels[cat] }));
}

/**
 * Generate insights from available data.
 */
function generateInsights({ weeklyStats, currentStreak, totalXP, tasksCompleted }) {
  const insights = [];

  if (currentStreak >= 7) {
    insights.push({ icon: '🔥', text: `Série de ${currentStreak} jours ! Tu es en feu !` });
  } else if (currentStreak >= 3) {
    insights.push({ icon: '⭐', text: `Belle série de ${currentStreak} jours — continue !` });
  }

  const thisWeek = weeklyStats?.tasksCompleted || 0;
  if (thisWeek >= 10) {
    insights.push({ icon: '🚀', text: `${thisWeek} tâches cette semaine — excellente semaine !` });
  } else if (thisWeek >= 5) {
    insights.push({ icon: '💪', text: `${thisWeek} tâches complétées cette semaine — bon rythme !` });
  }

  if (totalXP >= 1000) {
    insights.push({ icon: '🏆', text: `${totalXP} XP accumulés — tu es un champion !` });
  }

  if (insights.length === 0) {
    insights.push({ icon: '🌱', text: 'Commence à compléter des tâches pour voir tes insights !' });
  }

  return insights;
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * StatsView - Full statistics view with SVG charts.
 * Extends StatsPanel with richer visualizations.
 */
export const StatsView = memo(function StatsView({
  totalXP,
  todayXP,
  level,
  levelProgress,
  currentStreak,
  longestStreak,
  tasksCompleted,
  vitalTasksCompleted,
  unlockedBadges,
  weeklyStats,
  focusTime,
  proofsCount,
  recentXPGains = [],
  onClose
}) {
  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  const scrollRef = useScrollPosition('stats');

  const daily7 = useMemo(() => getDailyCompletions(7), []);
  const weekly12 = useMemo(() => getWeeklyCompletions(12), []);
  const categoryData = useMemo(() => getCategoryBreakdown(), []);
  const insights = useMemo(() =>
    generateInsights({ weeklyStats, currentStreak, totalXP, tasksCompleted }),
    [weeklyStats, currentStreak, totalXP, tasksCompleted]
  );

  const { unlocked, locked } = useMemo(() => {
    const all = Object.values(BADGES);
    return {
      unlocked: all.filter(b => unlockedBadges?.includes(b.id)),
      locked: all.filter(b => !unlockedBadges?.includes(b.id)).slice(0, 4)
    };
  }, [unlockedBadges]);

  return (
    <motion.div
      className={cn('fixed inset-0 flex flex-col', Z_CLASS.STATS_PANEL, 'bg-[var(--bg-base,#09090b)]')}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
    >
      {/* Header */}
      <div className="bg-[var(--bg-surface,#0c0c0f)] px-4 py-3 flex items-center justify-between border-b border-white/[0.06] flex-shrink-0 pt-safe">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-zinc-400" />
          <h2 className="text-sm font-medium text-zinc-100">Statistiques</h2>
        </div>
        <motion.button
          onClick={handleClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          whileTap={{ scale: 0.9 }}
        >
          <X className="w-4 h-4 text-zinc-400" />
        </motion.button>
      </div>

      {/* Scrollable content */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-24">
        <div className="max-w-md mx-auto px-4 py-4 space-y-5">

          {/* Summary cards */}
          <SummaryCards
            totalXP={totalXP} todayXP={todayXP} level={level} levelProgress={levelProgress}
            currentStreak={currentStreak} longestStreak={longestStreak}
            tasksCompleted={tasksCompleted} vitalTasksCompleted={vitalTasksCompleted}
            focusTime={focusTime} proofsCount={proofsCount}
          />

          {/* Insights */}
          {insights.length > 0 && (
            <Section title="Insights" icon={TrendingUp}>
              <div className="space-y-2">
                {insights.map((ins, i) => (
                  <motion.div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-zinc-900/60 border border-zinc-800/50 rounded-lg"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.08 }}
                  >
                    <span className="text-lg">{ins.icon}</span>
                    <p className="text-sm text-zinc-300">{ins.text}</p>
                  </motion.div>
                ))}
              </div>
            </Section>
          )}

          {/* 7-day bar chart */}
          <Section title="7 derniers jours" icon={Calendar}>
            <BarChart data={daily7} />
          </Section>

          {/* 12-week heatmap */}
          <Section title="12 semaines (activité)" icon={Flame}>
            <WeekHeatmap weeks={weekly12} />
          </Section>

          {/* Category donut */}
          {categoryData.length > 0 && (
            <Section title="Répartition par catégorie" icon={Target}>
              <DonutChart data={categoryData} />
            </Section>
          )}

          {/* XP history */}
          {recentXPGains.length > 0 && (
            <Section title="XP récents" icon={Zap}>
              <XPGainsList gains={recentXPGains} />
            </Section>
          )}

          {/* Badges */}
          <Section title={`Badges (${unlocked.length})`} icon={Award}>
            {unlocked.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {unlocked.map((badge, i) => (
                  <motion.div
                    key={badge.id}
                    className="flex flex-col items-center gap-1 p-2 bg-zinc-900/60 border border-zinc-800/50 rounded-lg"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.04 }}
                    title={badge.description}
                  >
                    <span className="text-xl">{badge.icon}</span>
                    <span className="text-[9px] text-zinc-500 text-center truncate w-full leading-tight">
                      {badge.name}
                    </span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-600 text-center py-3">
                Complète des tâches pour débloquer des badges !
              </p>
            )}

            {locked.length > 0 && (
              <div className="mt-3 space-y-1.5">
                <p className="text-[10px] text-zinc-600 uppercase tracking-wide mb-2">Prochains badges</p>
                {locked.map(badge => (
                  <div key={badge.id} className="flex items-center gap-3 p-2 bg-zinc-900/40 rounded-lg opacity-50">
                    <span className="text-base">🔒</span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-zinc-400">{badge.name}</p>
                      <p className="text-[10px] text-zinc-600 truncate">{badge.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

        </div>
      </div>
    </motion.div>
  );
});

// ─── Summary cards ─────────────────────────────────────────────────────────────

const SummaryCards = memo(function SummaryCards({
  totalXP, todayXP, level, levelProgress, currentStreak, longestStreak,
  tasksCompleted, vitalTasksCompleted, focusTime, proofsCount
}) {
  return (
    <div className="space-y-3">
      {/* Level + XP bar */}
      <div className="p-4 bg-zinc-900/60 border border-zinc-800/50 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-zinc-800 flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-zinc-100">{level}</span>
            <span className="text-[9px] text-zinc-500 uppercase tracking-wide">Niv.</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex justify-between text-xs mb-2">
              <span className="font-medium text-zinc-200">{totalXP} XP total</span>
              <span className="text-zinc-500">+{todayXP} aujourd'hui</span>
            </div>
            <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${levelProgress}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">Prochain niveau : {Math.round(levelProgress)}%</p>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2">
        <MiniStatCard
          icon={Flame}
          value={`${currentStreak}j`}
          label="Série actuelle"
          sub={`Record : ${longestStreak}j`}
          iconColor="text-amber-500"
        />
        <MiniStatCard
          icon={Target}
          value={tasksCompleted}
          label="Tâches complétées"
          sub={`${vitalTasksCompleted} vitaux`}
          iconColor="text-red-400"
        />
        {focusTime > 0 && (
          <MiniStatCard
            icon={Clock}
            value={`${focusTime}m`}
            label="Temps focus"
            iconColor="text-cyan-400"
          />
        )}
        {proofsCount > 0 && (
          <MiniStatCard
            icon={Trophy}
            value={proofsCount}
            label="Preuves ajoutées"
            iconColor="text-purple-400"
          />
        )}
      </div>
    </div>
  );
});

const MiniStatCard = memo(function MiniStatCard({ icon: Icon, value, label, sub, iconColor }) {
  return (
    <div className="p-3 bg-zinc-900/60 border border-zinc-800/50 rounded-xl">
      <Icon className={`w-4 h-4 ${iconColor || 'text-zinc-500'} mb-2`} />
      <p className="text-lg font-bold text-zinc-100">{value}</p>
      <p className="text-[11px] text-zinc-500">{label}</p>
      {sub && <p className="text-[10px] text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
});

// ─── Section wrapper ───────────────────────────────────────────────────────────

const Section = memo(function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-zinc-500" />
        <h3 className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
});

// ─── Bar chart (7 days) ───────────────────────────────────────────────────────

const BarChart = memo(function BarChart({ data }) {
  const max = Math.max(...data.map(d => d.count), 1);

  return (
    <div
      className="p-3 bg-zinc-900/60 border border-zinc-800/50 rounded-xl"
      role="img"
      aria-label="Graphique de complétion des 7 derniers jours"
    >
      <div className="flex items-end gap-1.5 h-24">
        {data.map((d, i) => {
          const pct = (d.count / max) * 100;
          const color = d.count === 0
            ? 'bg-zinc-800'
            : pct >= 70 ? 'bg-emerald-500'
            : pct >= 40 ? 'bg-amber-500'
            : 'bg-red-500';

          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[9px] text-zinc-600 tabular-nums">
                {d.count > 0 ? d.count : ''}
              </span>
              <div className="w-full flex flex-col justify-end" style={{ height: '64px' }}>
                <motion.div
                  className={`w-full rounded-sm ${color} ${d.isToday ? 'ring-1 ring-white/20' : ''}`}
                  initial={{ height: 0 }}
                  animate={{ height: `${Math.max(pct, d.count > 0 ? 8 : 0)}%` }}
                  transition={{ duration: 0.5, delay: i * 0.05, ease: 'easeOut' }}
                  title={`${d.dateStr} : ${d.count} tâche(s)`}
                />
              </div>
              <span className={`text-[9px] ${d.isToday ? 'text-zinc-300 font-medium' : 'text-zinc-600'}`}>
                {d.weekday}
              </span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mt-2 justify-center">
        <LegendDot color="bg-emerald-500" label=">70%" />
        <LegendDot color="bg-amber-500" label="40-70%" />
        <LegendDot color="bg-red-500" label="<40%" />
      </div>
    </div>
  );
});

const LegendDot = memo(function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1">
      <div className={`w-2 h-2 rounded-sm ${color}`} />
      <span className="text-[9px] text-zinc-600">{label}</span>
    </div>
  );
});

// ─── Week heatmap (12 weeks) ──────────────────────────────────────────────────

const WeekHeatmap = memo(function WeekHeatmap({ weeks }) {
  const max = Math.max(...weeks.map(w => w.count), 1);

  const getColor = (count) => {
    if (count === 0) return 'bg-zinc-800';
    const ratio = count / max;
    if (ratio >= 0.75) return 'bg-emerald-400';
    if (ratio >= 0.5) return 'bg-emerald-600';
    if (ratio >= 0.25) return 'bg-emerald-800';
    return 'bg-emerald-950';
  };

  return (
    <div
      className="p-3 bg-zinc-900/60 border border-zinc-800/50 rounded-xl"
      role="img"
      aria-label="Heatmap de productivité sur 12 semaines"
    >
      <div className="flex gap-1 overflow-x-auto pb-1">
        {weeks.map((week, i) => (
          <div
            key={i}
            className={`flex-shrink-0 w-7 h-7 rounded ${getColor(week.count)} transition-colors`}
            title={`Semaine ${week.weekOffset === 0 ? 'en cours' : Math.abs(week.weekOffset) + ' sem. passée(s)'} : ${week.count} tâches`}
          />
        ))}
      </div>
      <p className="text-[10px] text-zinc-600 mt-2 text-right">← il y a 12 sem. &nbsp;·&nbsp; aujourd'hui →</p>
    </div>
  );
});

// ─── Donut chart ───────────────────────────────────────────────────────────────

const DonutChart = memo(function DonutChart({ data }) {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) return null;

  // SVG donut: compute stroke-dasharray for each segment
  const r = 40;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  const segments = data.map(d => {
    const pct = d.count / total;
    const dash = pct * circ;
    const seg = { ...d, dash, offset, pct };
    offset += dash;
    return seg;
  });

  return (
    <div
      className="p-3 bg-zinc-900/60 border border-zinc-800/50 rounded-xl flex items-center gap-4"
      role="img"
      aria-label="Répartition des tâches par catégorie"
    >
      {/* SVG donut */}
      <svg viewBox="0 0 100 100" className="w-24 h-24 flex-shrink-0 -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#27272a" strokeWidth="18" />
        {segments.map((seg, i) => (
          <motion.circle
            key={i}
            cx="50" cy="50" r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth="18"
            strokeDasharray={`${seg.dash} ${circ - seg.dash}`}
            strokeDashoffset={-seg.offset}
            initial={{ strokeDasharray: `0 ${circ}` }}
            animate={{ strokeDasharray: `${seg.dash} ${circ - seg.dash}` }}
            transition={{ duration: 0.6, delay: i * 0.1, ease: 'easeOut' }}
          />
        ))}
      </svg>

      {/* Legend */}
      <div className="flex-1 space-y-1.5 min-w-0">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-xs text-zinc-300 truncate">{seg.label}</span>
            <span className="text-xs text-zinc-500 ml-auto tabular-nums">
              {Math.round(seg.pct * 100)}%
            </span>
          </div>
        ))}
        <p className="text-[10px] text-zinc-600 pt-1">{total} tâche(s) au total</p>
      </div>
    </div>
  );
});

// ─── XP gains list ────────────────────────────────────────────────────────────

const SOURCE_LABELS = {
  task: 'Tâche', vital: 'Vital', chore: 'Corvée', start: 'Démarrage',
  streak: 'Série', focus: 'Focus', proof: 'Preuve'
};

const XPGainsList = memo(function XPGainsList({ gains }) {
  const grouped = useMemo(() => {
    const byDay = {};
    gains.forEach(g => {
      const day = new Date(g.timestamp).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
      if (!byDay[day]) byDay[day] = { day, total: 0, entries: [] };
      byDay[day].total += g.amount;
      byDay[day].entries.push(g);
    });
    return Object.values(byDay).reverse();
  }, [gains]);

  return (
    <div className="p-3 bg-zinc-900/60 border border-zinc-800/50 rounded-xl space-y-2">
      {grouped.map((group, i) => (
        <div key={i}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-zinc-500 capitalize">{group.day}</span>
            <span className="text-[10px] font-medium text-amber-400">+{group.total} XP</span>
          </div>
          {group.entries.map((entry, j) => (
            <div key={j} className="flex items-center justify-between pl-2 py-0.5">
              <span className="text-[10px] text-zinc-600">
                {SOURCE_LABELS[entry.source] || entry.source}
              </span>
              <span className="text-[10px] text-zinc-500">+{entry.amount}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

export default StatsView;
